// 📁 contexts/DashboardContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';

const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // ✅ NUEVO: Estados para los indicadores precalculados
  const [indicadores, setIndicadores] = useState({
    menores_total: 0,
    menores_entidad: 0,
    menores_nuevos: 0,
    menores_vigentes: 0,
    menores_proximas: 0
  });
  const [indicadoresLoading, setIndicadoresLoading] = useState(false);
  const [indicadoresError, setIndicadoresError] = useState(null);
  const [indicadoresLastUpdate, setIndicadoresLastUpdate] = useState(null);

  // ✅ CONSTANTE PARA EL TIEMPO DE CACHÉ (10 minutos)
  const CACHE_DURATION = 60 * 60 * 1000;

  // ✅ FUNCIÓN para verificar si los datos en caché son válidos
  const isCacheValid = useCallback(() => {
    try {
      const cachedTimestamp = localStorage.getItem('dashboard_cache_timestamp');
      if (!cachedTimestamp) {
        return false;
      }
      
      const cacheTime = new Date(cachedTimestamp);
      const now = new Date();
      const timeDiff = now.getTime() - cacheTime.getTime();
      
      return timeDiff < CACHE_DURATION;
    } catch (error) {
      console.error('❌ Error verificando caché:', error);
      return false;
    }
  }, [CACHE_DURATION]);

  // ✅ FUNCIÓN para verificar si los indicadores en caché son válidos
  const isIndicadoresCacheValid = useCallback(() => {
    try {
      const cachedTimestamp = localStorage.getItem('indicadores_dashboard_cache_timestamp');
      if (!cachedTimestamp) {
        return false;
      }
      
      const cacheTime = new Date(cachedTimestamp);
      const now = new Date();
      const timeDiff = now.getTime() - cacheTime.getTime();
      
      return timeDiff < CACHE_DURATION;
    } catch (error) {
      console.error('❌ Error verificando caché de indicadores:', error);
      return false;
    }
  }, [CACHE_DURATION]);

  // ✅ FUNCIÓN para cargar datos desde el caché
  const loadFromCache = useCallback(() => {
    try {
      console.log('🔍 Cargando desde caché global...');
      
      const cachedData = localStorage.getItem('dashboard_cache_data');
      const cachedTimestamp = localStorage.getItem('dashboard_cache_timestamp');
      
      if (cachedData && cachedTimestamp) {
        const parsedData = JSON.parse(cachedData);
        setData(parsedData);
        setLastUpdate(new Date(cachedTimestamp));
        
        console.log(`📦 ✅ Datos cargados desde caché: ${parsedData.length} registros`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Error cargando desde caché:', error);
      localStorage.removeItem('dashboard_cache_data');
      localStorage.removeItem('dashboard_cache_timestamp');
      return false;
    }
  }, []);

  // ✅ NUEVA FUNCIÓN para cargar indicadores desde el caché
  const loadIndicadoresFromCache = useCallback(() => {
    try {
      console.log('🔍 Cargando indicadores desde caché...');
      
      const cachedData = localStorage.getItem('indicadores_dashboard_cache_data');
      const cachedTimestamp = localStorage.getItem('indicadores_dashboard_cache_timestamp');
      
      if (cachedData && cachedTimestamp) {
        const parsedData = JSON.parse(cachedData);
        setIndicadores(parsedData);
        setIndicadoresLastUpdate(new Date(cachedTimestamp));
        
        console.log('📊 ✅ Indicadores cargados desde caché:', parsedData);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Error cargando indicadores desde caché:', error);
      localStorage.removeItem('indicadores_dashboard_cache_data');
      localStorage.removeItem('indicadores_dashboard_cache_timestamp');
      return false;
    }
  }, []);

  // ✅ FUNCIÓN para guardar datos en el caché
  const saveToCache = useCallback((dataToCache) => {
    try {
      const timestamp = new Date().toISOString();
      localStorage.setItem('dashboard_cache_data', JSON.stringify(dataToCache));
      localStorage.setItem('dashboard_cache_timestamp', timestamp);
      console.log(`💾 Datos guardados en caché: ${dataToCache.length} registros`);
    } catch (error) {
      console.error('Error guardando en caché:', error);
    }
  }, []);

  // ✅ NUEVA FUNCIÓN para guardar indicadores en el caché
  const saveIndicadoresToCache = useCallback((indicadoresToCache) => {
    try {
      const timestamp = new Date().toISOString();
      localStorage.setItem('indicadores_dashboard_cache_data', JSON.stringify(indicadoresToCache));
      localStorage.setItem('indicadores_dashboard_cache_timestamp', timestamp);
      console.log('💾 Indicadores guardados en caché:', indicadoresToCache);
    } catch (error) {
      console.error('Error guardando indicadores en caché:', error);
    }
  }, []);

  // ✅ NUEVA FUNCIÓN para cargar indicadores desde Supabase
  const loadIndicadoresFromSupabase = useCallback(async () => {
    console.log('🌐 Cargando indicadores desde Supabase...');
    
    try {
      const { data: indicadoresData, error } = await supabase
        .from('indicadores_dashboard')
        .select(`
          menores_total,
          menores_entidad,
          menores_nuevos,
          menores_vigentes,
          menores_proximas
        `)
        .single(); // Solo hay un registro

      if (error) {
        throw error;
      }

      console.log('✅ Indicadores obtenidos de Supabase:', indicadoresData);
      return indicadoresData || {
        menores_total: 0,
        menores_entidad: 0,
        menores_nuevos: 0,
        menores_vigentes: 0,
        menores_proximas: 0
      };
    } catch (error) {
      console.error('❌ Error al cargar indicadores de Supabase:', error);
      throw error;
    }
  }, []);

  // ✅ FUNCIÓN para cargar datos desde Supabase (mantenida para compatibilidad)
  const loadFromSupabase = useCallback(async () => {
    console.log('🌐 Cargando datos...');
    
    try {
      let allData = [];
      let from = 0;
      const limit = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: contratos, error } = await supabase
          .from('contratos_menores')
          .select(`
            cod_contratacion,
            tipo,
            departamento,
            provincia,
            distrito,
            entidad,
            descripcion,
            inicio_cotizacion,
            fin_cotizacion,
            fecha_publicacion,
            url_publico_tdr,
            convocatorias_hoy
          `)
          .range(from, from + limit - 1);

        if (error) {
          throw error;
        }

        if (contratos && contratos.length > 0) {
          allData = [...allData, ...contratos];
          from += limit;
          hasMore = contratos.length === limit;
        } else {
          hasMore = false;
        }
      }

      console.log(`✅ Datos obtenidos de Supabase: ${allData.length} registros`);

      const formateado = allData.map((row) => ({
        'COD CONTRATACIÓN': row.cod_contratacion,
        TIPO: row.tipo,
        DPTO: row.departamento,
        PROVINCIA: row.provincia,
        DISTRITO: row.distrito,
        ENTIDAD: row.entidad,
        DESCRIPCIÓN: row.descripcion,
        'INICIO COTIZACIÓN': row.inicio_cotizacion,
        'FIN COTIZACIÓN': row.fin_cotizacion,
        'FECHA PUBLICACIÓN': row.fecha_publicacion,
        'URL_TDR': row.url_publico_tdr,
        'CONVOCATORIAS_HOY': row.convocatorias_hoy
      }));

      return formateado;
    } catch (error) {
      console.error('❌ Error al cargar datos de Supabase:', error);
      throw error;
    }
  }, []);

  // ✅ NUEVA FUNCIÓN principal para cargar indicadores
  const loadIndicadores = useCallback(async (forceRefresh = false) => {
    console.log(`🔄 loadIndicadores llamado. forceRefresh: ${forceRefresh}`);
    
    // Si ya tenemos indicadores válidos y no es refresh forzado, no hacer nada
    if (!forceRefresh && indicadores.menores_total > 0 && isIndicadoresCacheValid()) {
      console.log('⚡ Ya tenemos indicadores válidos en contexto');
      return;
    }
    
    // Si no es refresh forzado, verificar caché primero
    if (!forceRefresh && indicadores.menores_total === 0) {
      if (isIndicadoresCacheValid()) {
        console.log('⚡ Caché de indicadores válido - Carga INSTANTÁNEA');
        const cacheLoaded = loadIndicadoresFromCache();
        if (cacheLoaded) {
          setIndicadoresError(null);
          return;
        }
      } else {
        console.log('🕒 Caché de indicadores expirado, cargando como placeholder...');
        loadIndicadoresFromCache(); // Cargar caché expirado como placeholder
      }
    }

    // Cargar desde servidor
    console.log('🌐 Cargando indicadores desde servidor...');
    setIndicadoresLoading(true);
    setIndicadoresError(null);
    
    try {
      const freshIndicadores = await loadIndicadoresFromSupabase();
      
      setIndicadores(freshIndicadores);
      const updateTime = new Date();
      setIndicadoresLastUpdate(updateTime);
      
      saveIndicadoresToCache(freshIndicadores);
      
      console.log('✅ Indicadores frescos cargados:', freshIndicadores);
    } catch (error) {
      console.error('❌ Error al cargar indicadores:', error);
      setIndicadoresError('Error al cargar indicadores. Por favor, intente nuevamente.');
      
      if (indicadores.menores_total === 0) {
        const fallbackLoaded = loadIndicadoresFromCache();
        if (fallbackLoaded) {
          setIndicadoresError('Mostrando indicadores en caché. Algunos datos pueden no estar actualizados.');
        }
      }
    } finally {
      setIndicadoresLoading(false);
    }
  }, [isIndicadoresCacheValid, loadIndicadoresFromCache, loadIndicadoresFromSupabase, saveIndicadoresToCache, indicadores.menores_total]);

  // ✅ FUNCIÓN principal de carga de datos (mantenida para compatibilidad)
  const loadData = useCallback(async (forceRefresh = false) => {
    console.log(`🔄 loadData llamado. forceRefresh: ${forceRefresh}`);
    
    // Si ya tenemos datos válidos y no es refresh forzado, no hacer nada
    if (!forceRefresh && data.length > 0 && isCacheValid()) {
      console.log('⚡ Ya tenemos datos válidos en contexto');
      return;
    }
    
    // Si no es refresh forzado, verificar caché primero
    if (!forceRefresh && !data.length) {
      if (isCacheValid()) {
        console.log('⚡ Caché válido - Carga INSTANTÁNEA');
        const cacheLoaded = loadFromCache();
        if (cacheLoaded) {
          setError(null);
          setInitialized(true);
          return;
        }
      } else {
        console.log('🕒 Caché expirado, cargando como placeholder...');
        loadFromCache(); // Cargar caché expirado como placeholder
      }
    }

    // Cargar desde servidor
    console.log('🌐 Cargando desde servidor...');
    setLoading(true);
    setRefreshing(true);
    setError(null);
    
    try {
      const freshData = await loadFromSupabase();
      
      setData(freshData);
      const updateTime = new Date();
      setLastUpdate(updateTime);
      setInitialized(true);
      
      saveToCache(freshData);
      
      console.log(`✅ Datos frescos cargados: ${freshData.length} registros`);
    } catch (error) {
      console.error('❌ Error al cargar datos:', error);
      setError('Error al cargar los datos. Por favor, intente nuevamente.');
      
      if (data.length === 0) {
        const fallbackLoaded = loadFromCache();
        if (fallbackLoaded) {
          setError('Mostrando datos en caché. Algunos datos pueden no estar actualizados.');
          setInitialized(true);
        }
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isCacheValid, loadFromCache, loadFromSupabase, saveToCache, data.length]);

  // ✅ FUNCIÓN para refresh manual (ahora incluye indicadores)
  const handleRefresh = useCallback(() => {
    console.log('🔄 Refresh manual solicitado');
    loadData(true);
    loadIndicadores(true); // ✅ TAMBIÉN refrescar indicadores
  }, [loadData, loadIndicadores]);

  // ✅ INICIALIZACIÓN automática (ahora incluye indicadores)
  useEffect(() => {
    if (!initialized) {
      console.log('🚀 Inicializando DashboardContext...');
      loadData(false);
      loadIndicadores(false); // ✅ TAMBIÉN cargar indicadores
    }
  }, [initialized, loadData, loadIndicadores]);

  // ✅ ACTUALIZACIÓN automática cada 10 minutos (ahora incluye indicadores)
  useEffect(() => {
    if (!initialized) return;

    const interval = setInterval(() => {
      if (!isCacheValid()) {
        console.log('🔄 Caché expirado - Actualizando automáticamente...');
        loadData(false);
      }
      
      if (!isIndicadoresCacheValid()) {
        console.log('🔄 Caché de indicadores expirado - Actualizando automáticamente...');
        loadIndicadores(false);
      }
    }, 60000); // Verificar cada minuto

    return () => clearInterval(interval);
  }, [initialized, isCacheValid, isIndicadoresCacheValid, loadData, loadIndicadores]);

  const value = {
    data,
    loading,
    refreshing,
    error,
    lastUpdate,
    initialized,
    handleRefresh,
    isCacheValid,
    loadData,
    
    // ✅ NUEVOS valores para indicadores
    indicadores,
    indicadoresLoading,
    indicadoresError,
    indicadoresLastUpdate,
    
    // ✅ ESTADÍSTICAS AHORA USANDO INDICADORES PRECALCULADOS
    stats: React.useMemo(() => {
      return {
        totalConvocatorias: indicadores.menores_total || 0,
        totalEntidades: indicadores.menores_entidad || 0,
        convocatoriasVigentes: indicadores.menores_vigentes || 0,
        convocatoriasNuevas: indicadores.menores_nuevos || 0,
        convocatoriasProximas: indicadores.menores_proximas || 0
      };
    }, [indicadores])
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};